import { ReactElement, useState, useMemo } from 'react';
import { SmsSubscription } from '@/api';
import { useCompanyContext } from '../providers';
import {
    useDisableSmsAction,
    useEnableSmsAction,
    useOpenPaymentMethodPortalAction,
    useOpenSubscriptionPortalAction
} from '../actions';
import {
    UIButton,
    UIMenuItemProps,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISection,
    UISectionContent,
    UISectionHeader,
    UISpinner,
    UIText
} from '@/ui';
import { blue, moonlight } from '@/ui/styles';
import { useFetchSmsUsage } from '../queries';
import { useFormatDate } from '@/common/hooks';
import { Trans } from 'react-i18next';
import { useCompaniesTranslation } from '../hooks';

type SmsSubscriptionSettingsSectionProps = {
    smsSubscription: SmsSubscription | null;
    canEdit: boolean;
};

export function SmsSubscriptionSettingsSection(
    props: SmsSubscriptionSettingsSectionProps
): ReactElement {
    const { smsSubscription, canEdit } = props;

    const { company } = useCompanyContext();

    const [
        showFailedToEnableSmsDueToNoPaymentMethod,
        setShowFailedToEnableSmsDueToNoPaymentMethod
    ] = useState(false);

    const enableSms = useEnableSmsAction({
        companyId: company.id,
        // Nothing on the company says whether a card is on file for SMS, so the
        // prompt to add one is only raised once the API has refused.
        onPaymentMethodRequired: () =>
            setShowFailedToEnableSmsDueToNoPaymentMethod(true)
    });
    const disableSms = useDisableSmsAction({ companyId: company.id });
    const managePaymentMethod = useOpenPaymentMethodPortalAction({
        companyId: company.id
    });
    const viewInvoices = useOpenSubscriptionPortalAction({
        companyId: company.id
    });

    function onEnableSmsSubscription() {
        enableSms.enableSms().catch(() => {}); // Error handled in the action
    }

    function onDisableSmsSubscription() {
        disableSms.disableSms().catch(() => {}); // Error handled in the action
    }

    function onManagePaymentMethod() {
        // Error handled in the action
        managePaymentMethod.openPaymentMethodPortal().catch(() => {});
    }

    function onViewInvoices() {
        // Error handled in the action
        viewInvoices.openSubscriptionPortal().catch(() => {});
    }

    return (
        <SmsSubscriptionSettingsSectionForm
            companyId={company.id}
            smsSubscription={smsSubscription}
            onEnableSms={onEnableSmsSubscription}
            onDisableSms={onDisableSmsSubscription}
            onManagePaymentMethod={onManagePaymentMethod}
            onViewInvoices={onViewInvoices}
            enableLoading={enableSms.loading}
            disableLoading={disableSms.loading}
            paymentMethodLoading={managePaymentMethod.loading}
            invoiceHistoryLoading={viewInvoices.loading}
            showFailedToEnableSmsDueToNoPaymentMethod={
                showFailedToEnableSmsDueToNoPaymentMethod
            }
            setShowFailedToEnableSmsDueToNoPaymentMethod={
                setShowFailedToEnableSmsDueToNoPaymentMethod
            }
            canEdit={canEdit}
        />
    );
}

type SmsSubscriptionSettingsSectionFormProps = {
    companyId: string;
    smsSubscription: SmsSubscription | null;
    onEnableSms: () => void;
    onDisableSms: () => void;
    onManagePaymentMethod: () => void;
    onViewInvoices: () => void;
    enableLoading: boolean;
    disableLoading: boolean;
    paymentMethodLoading: boolean;
    invoiceHistoryLoading: boolean;
    showFailedToEnableSmsDueToNoPaymentMethod: boolean;
    setShowFailedToEnableSmsDueToNoPaymentMethod: (value: boolean) => void;
    canEdit: boolean;
};

function SmsSubscriptionSettingsSectionForm(
    props: SmsSubscriptionSettingsSectionFormProps
): ReactElement {
    const {
        companyId,
        smsSubscription,
        onEnableSms,
        onDisableSms,
        onManagePaymentMethod,
        onViewInvoices,
        enableLoading,
        disableLoading,
        paymentMethodLoading,
        invoiceHistoryLoading,
        showFailedToEnableSmsDueToNoPaymentMethod,
        setShowFailedToEnableSmsDueToNoPaymentMethod,
        canEdit
    } = props;

    const { t } = useCompaniesTranslation();
    const { formatDateOnly } = useFormatDate();

    const subtitle = useMemo(() => {
        if (!smsSubscription) {
            return '';
        }

        const periodEndStr = formatDateOnly(
            new Date(smsSubscription.periodEnd)
        );

        switch (smsSubscription.status) {
            case 'active':
                if (smsSubscription.cancelAtPeriodEnd) {
                    return t(
                        'components.sms_subscription_settings_section.final_payment_on',
                        { date: periodEndStr }
                    );
                } else {
                    return t(
                        'components.sms_subscription_settings_section.next_payment_on',
                        { date: periodEndStr }
                    );
                }
            case 'trialing':
                return '';
            case 'incomplete':
                return t(
                    'components.sms_subscription_settings_section.status_incomplete'
                );
            case 'past_due':
                return t(
                    'components.sms_subscription_settings_section.payment_overdue',
                    { date: periodEndStr }
                );
            case 'paused':
                return t('components.sms_subscription_settings_section.paused');
            case 'canceled':
            case 'incomplete_expired':
            case 'unpaid':
                return t(
                    'components.sms_subscription_settings_section.status_terminated'
                );
        }
    }, [formatDateOnly, smsSubscription, t]);

    const isSmsActive = useMemo(() => {
        if (!smsSubscription || smsSubscription.cancelAtPeriodEnd) {
            return false;
        } else {
            return true;
        }
    }, [smsSubscription]);

    const rightMenuItems: UIMenuItemProps[] = useMemo(() => {
        if (!canEdit) {
            return [];
        }
        const items: UIMenuItemProps[] = [
            {
                text: t('shared.subscription.add_payment_method'),
                leftIconName: 'credit-card',
                onClick: onManagePaymentMethod,
                loading: paymentMethodLoading
            },
            {
                text: t('shared.subscription.invoice_history'),
                leftIconName: 'list',
                onClick: onViewInvoices,
                loading: invoiceHistoryLoading
            }
        ];

        return items;
    }, [
        onManagePaymentMethod,
        paymentMethodLoading,
        onViewInvoices,
        invoiceHistoryLoading,
        canEdit,
        t
    ]);
    function onSmsPaymentMethodErrorModelClose() {
        setShowFailedToEnableSmsDueToNoPaymentMethod(false);
    }

    return (
        <>
            <UISection>
                <UISectionHeader
                    title={t(
                        'components.sms_subscription_settings_section.title'
                    )}
                    subtitle={subtitle}
                    rightMenuItemsText={t('shared.labels.more')}
                    rightMenuItems={rightMenuItems}
                />

                <UISectionContent borderBottom={!!smsSubscription}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.sms_subscription_settings_section.title'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    <Trans
                                        ns="companies"
                                        i18nKey="components.sms_subscription_settings_section.billing_explainer"
                                        components={{
                                            a: (
                                                <a
                                                    href="https://www.safepointapp.com/sms-pricing"
                                                    target="_blank"
                                                    style={{ color: blue }}
                                                />
                                            )
                                        }}
                                    />
                                </UIText>
                            </div>

                            {canEdit && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1
                                    }}
                                >
                                    {isSmsActive ? (
                                        <UIButton
                                            text={t(
                                                'components.sms_subscription_settings_section.disable'
                                            )}
                                            variant="danger"
                                            onClick={onDisableSms}
                                            loading={disableLoading}
                                        />
                                    ) : (
                                        <UIButton
                                            text={t(
                                                'components.sms_subscription_settings_section.enable'
                                            )}
                                            onClick={onEnableSms}
                                            loading={enableLoading}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </UISectionContent>
                {smsSubscription && (
                    <IncludedMonthlyCreditsSection
                        companyId={companyId}
                        smsSubscription={smsSubscription}
                    />
                )}
            </UISection>
            <UIModal
                isOpen={showFailedToEnableSmsDueToNoPaymentMethod}
                onClose={onSmsPaymentMethodErrorModelClose}
            >
                <UIModalHeader
                    title={t(
                        'components.sms_subscription_settings_section.payment_required_title'
                    )}
                />
                <UIModalContent>
                    <UIText>
                        {t(
                            'components.sms_subscription_settings_section.billing_explainer'
                        )}
                    </UIText>
                    <br />
                    <UIText>
                        {t(
                            'components.sms_subscription_settings_section.payment_required_message'
                        )}
                    </UIText>
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            text: t('shared.subscription.add_payment_method'),
                            onClick: onManagePaymentMethod,
                            loading: paymentMethodLoading,
                            variant: 'primary'
                        },
                        {
                            text: t('shared.labels.close'),
                            onClick: onSmsPaymentMethodErrorModelClose
                        }
                    ]}
                />
            </UIModal>
        </>
    );
}

type IncludedMonthlyCreditsSectionProps = {
    companyId: string;
    smsSubscription: SmsSubscription;
};
function IncludedMonthlyCreditsSection(
    props: IncludedMonthlyCreditsSectionProps
): ReactElement {
    const { companyId, smsSubscription } = props;

    const { t } = useCompaniesTranslation();
    const { formatDateOnly } = useFormatDate();

    const { status, usage } = useFetchSmsUsage(companyId);

    const resetDateStr = useMemo(() => {
        return formatDateOnly(new Date(smsSubscription.periodEnd));
    }, [formatDateOnly, smsSubscription.periodEnd]);

    const creditsPercentage = useMemo(() => {
        if (usage !== undefined) {
            const percent = (usage / smsSubscription.creditsIncluded) * 100;
            return Math.min(100, Math.max(0, percent));
        } else {
            return 0;
        }
    }, [usage, smsSubscription.creditsIncluded]);

    function convertPenniesToPounds(value: number): string {
        // Convert pennies to pounds
        const pounds = value / 100;

        // Format the pounds to a string with two decimal places
        const formattedPounds = pounds.toFixed(2);

        // Add the £ symbol and return
        return `£${formattedPounds}`;
    }

    return (
        <UISectionContent>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        gap: 4
                    }}
                >
                    <UIText>
                        {t(
                            'components.sms_subscription_settings_section.included_credits'
                        )}
                    </UIText>
                    {status === 'success' && usage !== undefined ? (
                        <UIText type="default2">
                            {t(
                                'components.sms_subscription_settings_section.credits_used',
                                {
                                    used: convertPenniesToPounds(usage),
                                    total: convertPenniesToPounds(
                                        smsSubscription.creditsIncluded
                                    ),
                                    date: resetDateStr
                                }
                            )}
                        </UIText>
                    ) : (
                        <UISpinner />
                    )}
                </div>
                <div
                    style={{
                        height: 6,
                        maxHeight: 6,
                        width: '100%',
                        backgroundColor: moonlight,
                        borderRadius: 3
                    }}
                >
                    <div
                        style={{
                            height: 6,
                            maxHeight: 6,
                            width: `${creditsPercentage}%`,
                            backgroundColor: blue,
                            borderRadius: 3
                        }}
                    />
                </div>
            </div>
        </UISectionContent>
    );
}
